import React, { useMemo, useEffect } from 'react';
import { OptimizelyProvider as BaseOptimizelyProvider } from '@optimizely/react-sdk';
import optimizelyClient from './optimizelyClient';
import { useOptimizelyUserId } from './useOptimizelyUserId';
import { useUser } from '../auth/auth.service';
import { Security } from '../auth/auth.types';

interface OptimizelyProviderProps {
  children: React.ReactNode;
  /**
   * Optional callback when Optimizely client is ready
   */
  onReady?: () => void;
  /**
   * Optional callback when Optimizely client fails to initialize
   */
  onError?: (error: Error) => void;
}

/**
 * User attributes to be passed to Optimizely for audience targeting
 */
interface OptimizelyUserAttributes {
  userId?: string | number;
  loginId?: string;
  security?: Security;
  webAccess?: number;
  type?: string;
  [key: string]: any; // Allow for additional attributes
}

/**
 * Optimizely provider that automatically uses the current user ID
 * and provides user attributes for targeting.
 * 
 * This component wraps the base Optimizely provider and handles:
 * - User identification (authenticated or anonymous)
 * - User attributes from the current user session
 * - Client initialization status
 */
export const OptimizelyProvider: React.FC<OptimizelyProviderProps> = ({ 
  children, 
  onReady, 
  onError 
}) => {
  const userId = useOptimizelyUserId();
  const { data: userData } = useUser(true);
  
  // Check if Optimizely client is ready
  useEffect(() => {
    optimizelyClient.onReady({ timeout: 5000 })
      .then(() => {
        onReady?.();
      })
      .catch((error) => {
        console.error('Optimizely client failed to initialize', error);
        onError?.(error);
      });
  }, [onReady, onError]);
  
  // Create user attributes from user data
  const userAttributes = useMemo<OptimizelyUserAttributes>(() => {
    if (!userData?.data) {
      return {};
    }
    
    const { id, loginId, security, webAccess, type } = userData.data;
    
    // Only include attributes that have values
    const attributes: OptimizelyUserAttributes = {};
    
    if (id) attributes.userId = id;
    if (loginId) attributes.loginId = loginId;
    if (security) attributes.security = security;
    if (webAccess) attributes.webAccess = webAccess;
    if (type) attributes.type = type;
    
    return attributes;
  }, [userData?.data]); // Only depend on the data object, not the entire userData

  return (
    <BaseOptimizelyProvider
      optimizely={optimizelyClient}
      user={{
        id: userId || 'anonymous',
        attributes: userAttributes,
      }}
    >
      {children}
    </BaseOptimizelyProvider>
  );
};
